//#region ENUM TimeEnum
/**
 * ### TimeEnum
 * Enum representing common time intervals in milliseconds.
 *
 * This enum is accompanied by:
 * - `TimeEnumHumanReadable` — human-friendly time labels.
 * - `TimeType` — a union type of all enum keys.
 *
 * @enum TimeEnum
 * @property OneSecond - Represents 1 second (1000 ms).
 * @property FiveSeconds - Represents 5 seconds (5000 ms).
 * @property TenSeconds - Represents 10 seconds (10000 ms).
 * @property ThirtySeconds - Represents 30 seconds (30000 ms).
 * @property OneMinute - Represents 1 minute (60000 ms).
 * @property TwoMinutes - Represents 2 minutes (120000 ms).
 * @property FiveMinutes - Represents 5 minutes (300000 ms).
 * @property TenMinutes - Represents 10 minutes (600000 ms).
 * @property TwentyMinutes - Represents 20 minutes (1200000 ms).
 * @property ThirtyMinutes - Represents 30 minutes (1800000 ms).
 * @property OneHour - Represents 1 hour (3600000 ms).
 * @property OneDay - Represents 1 day (86400000 ms).
 */
export enum TimeEnum {
  /** Represents 1 second (1000 ms). */
  OneSecond = 1000,

  /** Represents 5 seconds (5000 ms). */
  FiveSeconds = 5 * 1000,

  /** Represents 10 seconds (10000 ms). */
  TenSeconds = 10 * 1000,

  /** Represents 30 seconds (30000 ms). */
  ThirtySeconds = 30 * 1000,

  /** Represents 1 minute (60000 ms). */
  OneMinute = 60 * 1000,

  /** Represents 2 minutes (120000 ms). */
  TwoMinutes = 2 * 60 * 1000,

  /** Represents 5 minutes (300000 ms). */
  FiveMinutes = 5 * 60 * 1000,

  /** Represents 5 minutes (360000 ms). */
  SixMinutes = 6 * 60 * 1000,

  /** Represents 10 minutes (600000 ms). */
  TenMinutes = 10 * 60 * 1000,

  /** Represents 20 minutes (1200000 ms). */
  TwentyMinutes = 20 * 60 * 1000,

  /** Represents 30 minutes (1800000 ms). */
  ThirtyMinutes = 30 * 60 * 1000,

  /** Represents 1 hour (3600000 ms). */
  OneHour = 60 * 60 * 1000,

  /** Represents 1 day (86400000 ms). */
  OneDay = 24 * 60 * 60 * 1000
}

//#endregion

//#region ENUM TimeEnumHumanReadable
/**
 * ### TimeEnumHumanReadable
 * Human-readable labels for each member of `TimeEnum`.
 *
 * @enum TimeEnumHumanReadable
 * @property OneSecond - '1 Second'
 * @property FiveSeconds - '5 Seconds'
 * @property TenSeconds - '10 Seconds'
 * @property ThirtySeconds - '30 Seconds'
 * @property OneMinute - '1 Minute'
 * @property TwoMinutes - '2 Minutes'
 * @property FiveMinutes - '5 Minutes'
 * @property TenMinutes - '10 Minutes'
 * @property TwentyMinutes - '20 Minutes'
 * @property ThirtyMinutes - '30 Minutes'
 * @property OneHour - '1 Hour'
 * @property OneDay - '1 Day'
 */
export enum TimeEnumHumanReadable {
  /** Human-readable label for 1 second. */
  OneSecond = '1 Second',

  /** Human-readable label for 5 seconds. */
  FiveSeconds = '5 Seconds',

  /** Human-readable label for 10 seconds. */
  TenSeconds = '10 Seconds',

  /** Human-readable label for 30 seconds. */
  ThirtySeconds = '30 Seconds',

  /** Human-readable label for 1 minute. */
  OneMinute = '1 Minute',

  /** Human-readable label for 2 minutes. */
  TwoMinutes = '2 Minutes',

  /** Human-readable label for 5 minutes. */
  FiveMinutes = '5 Minutes',

  /** Human-readable label for 10 minutes. */
  TenMinutes = '10 Minutes',

  /** Human-readable label for 20 minutes. */
  TwentyMinutes = '20 Minutes',

  /** Human-readable label for 30 minutes. */
  ThirtyMinutes = '30 Minutes',

  /** Human-readable label for 1 hour. */
  OneHour = '1 Hour',

  /** Human-readable label for 1 day. */
  OneDay = '1 Day'
}

//#endregion

//#region TYPE TimeType
/**
 * ### TimeType
 * Union type of all keys in `TimeEnum`.
 *
 * @type TimeType
 * @example
 * // Possible values: 'OneSecond' | 'FiveSeconds' | ... | 'OneDay'
 */
export type TimeType = keyof typeof TimeEnum;
//#endregion
